import React, { useState, useContext, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Pencil, ShieldCheck, Trash2, Search, X } from "lucide-react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import { getRoleList, getRoleById, createRole, updateRole, deleteRole } from "../../service/Role";
import { getAllByOrganization } from "../../service/User";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import CreateRoleModal from "./CreateRoleModal";
import UpdateRoleModal from "./UpdateRoleModal";
import PermissionModal from "./PermissionModal";
import { getPermissionByScreenCode, canShowActionColumn } from "../../utils/permissions";
import DeletePermission from "../../components/suspence/DeletePermission";
import ActionIconButton from "../../components/common/ActionIconButton";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import TableScrollShell from "../../components/common/TableScrollShell";
import useDirection from "../../hooks/useDirection";
import HoverTooltip from "../../components/common/HoverTooltip";
import SharedOverflowTooltipText from "../../components/common/OverflowTooltipText";
const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

const normalizeOverflowText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const OverflowTooltipText = ({ text, className = "" }) => (
  <SharedOverflowTooltipText text={normalizeOverflowText(text)} className={className} />
);

const RoleManagement = () => {
  const { theme, langMode, userType, selectedOrgId } = useContext(AppContext);
  const [roleList, setRoleList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [organizationList, setOrganizationList] = useState([]);
  const [loadingRoleDetail, setLoadingRoleDetail] = useState(false);
  const [loadingRoleId, setLoadingRoleId] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [roleForPermission, setRoleForPermission] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const isSuperAdmin = userType === "super_admin";
  const rolePerm = getPermissionByScreenCode("ROLE_MANAGEMENT");
  const showRoleActionColumn = isSuperAdmin || canShowActionColumn("ROLE_MANAGEMENT");
  const canCreateRole = isSuperAdmin || Boolean(rolePerm?.canCreate);
  const canUpdateRole = isSuperAdmin || Boolean(rolePerm?.canUpdate);
  const canDeleteRole = isSuperAdmin || Boolean(rolePerm?.canDelete);
  const { isRTL: isRtl } = useDirection();
  const searchQuery = searchTerm.trim().toLowerCase();
  const filteredRoleList = searchQuery
    ? roleList.filter((role) => {
      const orgName =
        organizationList.find((o) => (o.organizationId ?? o.id) === role.organizationId)?.organizationName ??
        role.organizationName ??
        "";
      return [role.roleName ?? role.name, role.description, orgName].some((v) =>
        String(v || "").toLowerCase().includes(searchQuery)
      );
    })
    : roleList;
  const startIndex = currentPage * pageSize;
  const totalItems = searchQuery ? filteredRoleList.length : totalValue;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const totalPages = Math.max(0, Math.ceil(totalItems / pageSize) - 1);
  const pagedRoleList = filteredRoleList.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handlePageSizeChange = (value) => {
    setPageSize(parseInt(value, 10));
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const [createForm, setCreateForm] = useState({
    roleName: "",
    description: "",
    organizationId: selectedOrgId,
    screenPermissions: {}, // { screenId: { canView, canCreate, canUpdate, canDelete } }
  });
  const [updateForm, setUpdateForm] = useState({
    roleName: "",
    description: "",
    organizationId: null,
    screenPermissions: {}, // { screenId: { canView, canCreate, canUpdate, canDelete } }
  });

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const orgId = isSuperAdmin ? selectedOrgId : undefined;
      const res = await getRoleList(orgId);
      const list = Array.isArray(res) ? res : res?.roleList ?? res?.content ?? [];
      const total = res?.totalSize ?? res?.totalElements ?? list.length;
      setRoleList(list);
      setTotalValue(total);
    } catch (err) {
      console.error(err);
      setRoleList([]);
      setTotalValue(0);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const list = await getAllByOrganization();
      setOrganizationList(Array.isArray(list) ? list : list?.organizationList ?? []);
    } catch (err) {
      console.error(err);
      setOrganizationList([]);
    }
  };

  useEffect(() => {
    fetchRoles();
    if (isSuperAdmin) fetchOrganizations();
  }, [selectedOrgId]);

  const handleCreate = async () => {
    if (!createForm.roleName?.trim()) {
      toast.warning(translations[langMode].roleName + " is required");
      return;
    }
    if (isSuperAdmin && !selectedOrgId) {
      toast.warning(translations[langMode].selectOrg + " is required");
      return;
    }
    setLoading(true);
    try {
      const screenPermissionsArray = Object.entries(createForm.screenPermissions || {}).map(([screenId, perms]) => ({
        screenId: parseInt(screenId, 10),
        ...perms,
      }));
      const payload = {
        name: createForm.roleName.trim(),
        description: (createForm.description || "").trim(),
        organizationId: isSuperAdmin ? selectedOrgId : undefined,
        screenPermissions: screenPermissionsArray,
      };
      const data = await createRole(payload);
      if (data?.header?.code === 600 || data?.id) {
        toast.success("Role created successfully");
        setCreateOpen(false);
        setCreateForm({ roleName: "", description: "", organizationId: selectedOrgId, screenPermissions: {} });
        fetchRoles();
      } else {
        const backendMessage =
          data?.body?.value ??
          data?.body?.error ??
          data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const openUpdate = async (role) => {
    const roleId = role?.id ?? role?.roleId;
    if (!roleId) return;
    setLoadingRoleDetail(true);
    setLoadingRoleId(roleId);
    try {
      const roleDetail = await getRoleById(roleId);
      setEditingRole(roleDetail);
      // Convert screenPermissions array to object format: { screenId: { canView, canCreate, canUpdate, canDelete } }
      const screenPermissionsObj = {};
      const permsList = roleDetail?.screenPermissions;
      if (Array.isArray(permsList)) {
        permsList.forEach((perm) => {
          const sid = perm.screenId;
          if (sid != null) {
            screenPermissionsObj[sid] = {
              canView: perm.canView ?? false,
              canCreate: perm.canCreate ?? false,
              canUpdate: perm.canUpdate ?? false,
              canDelete: perm.canDelete ?? false,
            };
          }
        });
      }
      setUpdateForm({
        roleName: roleDetail?.name ?? roleDetail?.roleName ?? "",
        description: roleDetail?.description ?? "",
        organizationId: roleDetail?.organizationId ?? null,
        screenPermissions: screenPermissionsObj,
      });
      if (isSuperAdmin && organizationList.length === 0) fetchOrganizations();
      setUpdateOpen(true);
    } catch (err) {
      console.error(err);
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoadingRoleDetail(false);
      setLoadingRoleId(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingRole?.id && !editingRole?.roleId) return;
    if (!updateForm.roleName?.trim()) {
      toast.warning(translations[langMode].roleName + " is required");
      return;
    }
    const id = editingRole.id ?? editingRole.roleId;
    setLoading(true);
    try {
      const screenPermissionsArray = Object.entries(updateForm.screenPermissions || {}).map(([screenId, perms]) => ({
        screenId: parseInt(screenId, 10),
        ...perms,
      }));
      const payload = {
        name: updateForm.roleName.trim(),
        description: (updateForm.description || "").trim(),
        organizationId: isSuperAdmin ? updateForm.organizationId : undefined,
        screenPermissions: screenPermissionsArray,
      };
      const data = await updateRole(id, payload);
      if (data?.header?.code === 600 || data?.id) {
        toast.success("Role updated successfully");
        setUpdateOpen(false);
        setEditingRole(null);
        fetchRoles();
      } else {
        const backendMessage =
          data?.body?.value ??
          data?.body?.error ??
          data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (role) => {
    setRoleToDelete(role);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;
    const id = roleToDelete.id ?? roleToDelete.roleId;
    setLoading(true);
    try {
      const data = await deleteRole(id);
      if (data?.header?.code === 600 || data === undefined) {
        toast.success("Role deleted successfully");
        closeDeleteModal();
        fetchRoles();
      } else {
        const backendMessage =
          data?.body?.value ??
          data?.body?.error ??
          data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const orgOptions = organizationList.map((org) => ({
    value: org.organizationId ?? org.id,
    label: org.organizationName ?? org.name ?? org.organizationId ?? org.id,
  }));

  const actionHeaderLabel = translations[langMode].Action;
  const tableHeaders = [
    "S.L",
    translations[langMode].roleName,
    translations[langMode].description,
    ...(isSuperAdmin ? [translations[langMode].OrganizationName] : []),
    ...(showRoleActionColumn ? [actionHeaderLabel] : []),
  ];

  const inputClass = `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`;

  return (
    <>
      <div className={`h-full min-h-0 w-full flex flex-col ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
        <div
          className={`mx-2 mt-2 min-w-0 shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm flex flex-wrap items-start justify-between gap-3 sm:mx-4 sm:mt-3 sm:px-5 sm:py-4 ${theme === "dark" ? "dark-mode " : theme === "high-contrast" ? "high-contrast " : ""}`}
        >
          <div className="w-full min-w-0 lg:w-auto lg:flex-1">
            <h1 className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
              {translations[langMode].roleManagement}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {translations[langMode]?.roleManagementSubtitle ?? "Create and manage roles with custom permissions for your organization."}
            </p>
          </div>
          <div className="relative w-full min-w-0 sm:w-64 sm:flex-none">
            <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRtl ? "right-2.5" : "left-2.5"}`} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={translations[langMode]?.search ?? "Search"}
              className={`w-full h-9 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${isRtl ? "pr-8 pl-7" : "pl-8 pr-7"} ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label={translations[langMode]?.clearSearch ?? "Clear search"}
                className={`absolute top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 ${isRtl ? "left-2.5" : "right-2.5"}`}
              >
                <X size={13} />
              </button>
            )}
          </div>
          {canCreateRole && (
            <CommonButton colorIntent="primary"
              variant="primary"
              tooltipText={translations[langMode].createRole}
              tooltipPosition="bottom"
              onClick={() => {
                setCreateForm((f) => ({ ...f, organizationId: selectedOrgId }));
                setCreateOpen(true);
              }}
              wrapperClassName="w-full sm:w-auto"
              className="!h-10 !min-h-[40px] !w-full !shrink-0 !gap-1.5 !rounded-lg !px-2.5 sm:!h-9 sm:!min-h-[36px] sm:!w-auto sm:!gap-2 sm:!px-3"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              <span className="text-sm font-medium">{translations[langMode].createRole}</span>
            </CommonButton>
          )}
        </div>

        <div className={`${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""} flex-1 min-h-0 w-full flex flex-col`}>

          <div className="flex-1 min-h-0 w-full flex flex-col">
            <div className="task-list-table mx-2 mb-2 mt-2 flex min-h-[24rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1">
              <div className="flex flex-wrap items-center justify-between border-b border-slate-100 bg-white px-2 py-2.5 sm:px-5">
                <div className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5 sm:mx-auto sm:mt-1 sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  {roleList.length > 0 && (
                    <>
                      <p className="flex items-center text-xs text-slate-500 sm:me-2 sm:gap-4 sm:whitespace-nowrap">
                        <span>{`Showing ${totalItems === 0 ? 0 : startIndex + 1}-${endIndex} of ${totalItems}`}</span>
                      </p>
                      <div className="contents text-xs text-slate-500 sm:ml-auto sm:flex sm:w-auto sm:items-center sm:justify-start sm:gap-3">
                        <div className="flex min-w-0 items-center justify-end gap-1.5">
                          <label htmlFor="rolePageSize" className="whitespace-nowrap">{translations[langMode].rows}</label>
                          <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                        </div>
                        <span className="pager-nav col-span-2 flex shrink-0 items-center justify-center gap-1 sm:col-auto">
                          <PagerArrowButton
                            direction="prev"
                            isRtl={isRtl}
                            onClick={goToPreviousPage}
                            disabled={currentPage === 0}
                            ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                          />
                          <span className="text-xs text-slate-600 px-1 whitespace-nowrap">{currentPage + 1} / {totalPages + 1}</span>
                          <PagerArrowButton
                            direction="next"
                            isRtl={isRtl}
                            onClick={goToNextPage}
                            disabled={currentPage >= totalPages}
                            ariaLabel={translations[langMode]?.nextPage || "Next page"}
                          />
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {loading && <Spinner />}
              <TableScrollShell
                theme={theme}
                className="flex-1 min-h-0"
                innerClassName="task-list-scrollbar h-full max-h-none rounded-none border-0 pb-3 shadow-none"
                maxHeight="none"
              >
                <table className="min-w-[760px] w-full text-xs text-left rtl:text-right text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-slate-700 border-b border-slate-200 dark:text-gray-400 sticky top-0 bg-slate-50 z-10">
                    <tr>
                      {tableHeaders.map((header, index) => (
                        <th
                          key={index}
                          scope="col"
                          className={
                            header === actionHeaderLabel
                              ? "w-[120px] min-w-[120px] max-w-[120px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-500 tracking-[0.04em] text-center"
                              : index === 0
                                ? "w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center"
                                : "px-2 py-3.5 text-xs font-semibold uppercase text-slate-500 tracking-[0.04em] text-center"
                          }
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {pagedRoleList && pagedRoleList.length > 0 ? (
                      pagedRoleList.map((role, index) => (
                        <tr key={role.id ?? role.roleId ?? index} className="text-xs border-b border-slate-100 hover:bg-slate-50/70 transition-colors duration-150 align-middle">
                          <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center align-middle">{startIndex + index + 1}</td>
                          <td className="px-2 py-2.5 max-w-[220px] text-xs font-normal text-center align-middle">
                            <OverflowTooltipText text={role.roleName ?? role.name ?? "-"} className="text-center" />
                          </td>
                          <td className="px-2 py-2.5 max-w-[260px] text-xs font-normal text-center align-middle">
                            <OverflowTooltipText text={role.description === "Text" ? "-" : (role.description ?? "-")} className="text-center" />
                          </td>
                          {isSuperAdmin && (
                            <td className="px-2 py-2.5 max-w-[180px] text-xs font-normal text-center align-middle">
                              <OverflowTooltipText
                                className="text-center"
                                text={
                                  organizationList.find((o) => (o.organizationId ?? o.id) === (role.organizationId ?? role.organizationId))?.organizationName ??
                                  role.organizationName ??
                                  role.organizationId ??
                                  "-"
                                }
                              />
                            </td>
                          )}
                          {showRoleActionColumn && (
                            <td className="px-2 py-2.5 whitespace-nowrap w-[120px] max-w-[120px] align-middle">
                              <div className="flex items-center justify-center gap-0.5">
                                {canUpdateRole && (
                                  <>
                                    <HoverTooltip text={loadingRoleId === (role.id ?? role.roleId) ? (translations[langMode]?.loadingData ?? "Loading...") : translations[langMode].updateRole}>
                                      <ActionIconButton
                                        onClick={() => openUpdate(role)}
                                        disabled={loadingRoleId !== null && (role.id ?? role.roleId) === loadingRoleId}
                                        tone="blue"
                                        ariaLabel={translations[langMode].updateRole}
                                        className="!h-7 !w-7 !rounded-md"
                                      >
                                        <Pencil size={14} />
                                      </ActionIconButton>
                                    </HoverTooltip>
                                    <HoverTooltip text={translations[langMode]?.permissions ?? "Permission"}>
                                      <ActionIconButton
                                        onClick={() => {
                                          setRoleForPermission(role);
                                          setPermissionModalOpen(true);
                                        }}
                                        tone="amber"
                                        ariaLabel={translations[langMode]?.permissions ?? "Permission"}
                                        className="!h-7 !w-7 !rounded-md"
                                      >
                                        <ShieldCheck size={14} />
                                      </ActionIconButton>
                                    </HoverTooltip>
                                  </>
                                )}
                                {canDeleteRole && (
                                  <HoverTooltip text={translations[langMode].deleteRole}>
                                    <ActionIconButton
                                      onClick={() => openDelete(role)}
                                      tone="red"
                                      ariaLabel={translations[langMode].deleteRole}
                                      className="!h-7 !w-7 !rounded-md"
                                    >
                                      <Trash2 size={14} />
                                    </ActionIconButton>
                                  </HoverTooltip>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={tableHeaders.length} className="text-center py-6 text-xs text-gray-500">
                          {translations[langMode].noRolesFound}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </TableScrollShell>
            </div>
          </div>
        </div>
      </div>

      <CreateRoleModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateForm({ roleName: "", description: "", organizationId: selectedOrgId, screenPermissions: {} });
        }}
        form={createForm}
        setForm={setCreateForm}
        onSave={handleCreate}
        loading={loading}
      />

      <UpdateRoleModal
        open={updateOpen}
        onClose={() => setUpdateOpen(false)}
        form={updateForm}
        setForm={setUpdateForm}
        orgOptions={orgOptions}
        isSuperAdmin={isSuperAdmin}
        onSave={handleUpdate}
        loading={loading}
      />

      <PermissionModal
        open={permissionModalOpen}
        onClose={() => {
          setPermissionModalOpen(false);
          setRoleForPermission(null);
        }}
        role={roleForPermission}
        onSaved={fetchRoles}
      />

      {/* Delete role - confirmation modal (must confirm before delete) */}
      {!!roleToDelete && deleteOpen && (
        <DeletePermission
          isLoading={loading}
          onClose={closeDeleteModal}
          deleteFunction={handleDelete}
          onDeleteText={`${translations[langMode].confirmDeleteRole}${roleToDelete?.roleName || roleToDelete?.name ? ` "${roleToDelete.roleName ?? roleToDelete.name}"` : ""}`}
        />
      )}
      <Transition.Root show={false} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDeleteModal}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className={`relative rounded-2xl shadow-xl sm:max-w-md w-full ${theme === "dark" ? "dark-mode bg-gray-800" : theme === "high-contrast" ? "high-contrast bg-white" : "bg-white"}`}>
                  <div className="px-5 py-6">
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {translations[langMode].deleteRole}
                    </Dialog.Title>
                    <p className="text-gray-700 dark:text-gray-200">
                      {translations[langMode].confirmDeleteRole}
                      {roleToDelete?.roleName != null && roleToDelete.roleName !== "" && (
                        <span className="font-medium block mt-2">"{roleToDelete.roleName}"</span>
                      )}
                    </p>
                  </div>
                  <div className="flex justify-end items-center gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-600">
                    <CommonButton variant="outline" onClick={closeDeleteModal}>{translations[langMode].cancle || "Cancel"}</CommonButton>
                    <CommonButton colorIntent="danger" variant="danger" onClick={handleDelete}>{translations[langMode].deleteRole}</CommonButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default RoleManagement;

